import { IDriverActiveOrder } from "../models/IDriver";
import { customerAddressText, hasCoords, normalizeDeliveryStatus } from "./utils";

export interface LatLng { lat: number; lng: number }

export interface RouteStop {
  key: string;
  kind: "pickup" | "dropoff";
  lat: number;
  lng: number;
  title: string;
  address: string;
  phone?: string | null;
  /** Bu durakta ilgili siparişler (restoran durağında birden fazla olabilir) */
  orders: IDriverActiveOrder[];
}

/** Kuş uçuşu mesafe (km) — yalnız sıralama için; gerçek süre Google rotasından gelir. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * Aktif siparişlerden durakları çıkarır:
 * - Henüz alınmamış (Assigned) siparişlerin restoranları → alış durağı (aynı restoran tek durak)
 * - Her siparişin müşterisi → teslim durağı
 * Konumu olmayan siparişler `missing` listesine düşer.
 */
export function buildStops(orders: IDriverActiveOrder[]) {
  const pickups = new Map<string, RouteStop>();
  const dropoffs: RouteStop[] = [];
  const missing: IDriverActiveOrder[] = [];

  orders.forEach((o) => {
    const status = normalizeDeliveryStatus(o.deliveryStatus);
    const addr = o.deliveryAddress;
    if (!addr || !hasCoords(addr.latitude, addr.longitude)) { missing.push(o); return; }

    if (status === "Assigned") {
      const r = o.restaurant;
      if (!hasCoords(r.latitude, r.longitude)) { missing.push(o); return; }
      const existing = pickups.get(r.id);
      if (existing) existing.orders.push(o);
      else pickups.set(r.id, {
        key: `p-${r.id}`, kind: "pickup", lat: r.latitude!, lng: r.longitude!,
        title: r.name ?? "Restoran", address: r.address ?? "", phone: r.phoneNo, orders: [o],
      });
    }

    dropoffs.push({
      key: `d-${o.deliveryId}`, kind: "dropoff", lat: addr.latitude!, lng: addr.longitude!,
      title: o.customer?.name || "Müşteri", address: customerAddressText(o), phone: o.customer?.telNo, orders: [o],
    });
  });

  return { pickups: Array.from(pickups.values()), dropoffs, missing };
}

/** En yakın komşu: her adımda bulunulan yere en yakın durağa git. */
export function nearestNeighbor<T extends LatLng>(start: LatLng, points: T[]): T[] {
  const left = points.slice();
  const out: T[] = [];
  let cur = start;
  while (left.length) {
    let best = 0;
    for (let i = 1; i < left.length; i++) {
      if (haversineKm(cur, left[i]) < haversineKm(cur, left[best])) best = i;
    }
    cur = left[best];
    out.push(left.splice(best, 1)[0]);
  }
  return out;
}

/**
 * Teslim sırası: az durakta bütün sıralamaları dener (en kısa toplam), çokta en yakın komşu.
 * 7 durak = 5040 deneme, anlık.
 */
export function bestOrder<T extends LatLng>(start: LatLng, points: T[]): T[] {
  if (points.length <= 1) return points.slice();
  if (points.length > 7) return nearestNeighbor(start, points);

  let best: T[] = points.slice();
  let bestCost = Infinity;
  const permute = (rest: T[], acc: T[], cost: number, cur: LatLng) => {
    if (cost >= bestCost) return;
    if (!rest.length) { bestCost = cost; best = acc; return; }
    rest.forEach((p, i) => {
      permute([...rest.slice(0, i), ...rest.slice(i + 1)], [...acc, p], cost + haversineKm(cur, p), p);
    });
  };
  permute(points, [], 0, start);
  return best;
}

/**
 * Rota sırası: önce alınacak restoranlar (yakından uzağa), sonra teslimatlar (en kısa toplam).
 * Teslimat, siparişi restorandan almadan önce sıraya giremez — bu yüzden iki aşama.
 */
export function planRoute(origin: LatLng, orders: IDriverActiveOrder[]) {
  const { pickups, dropoffs, missing } = buildStops(orders);
  const orderedPickups = nearestNeighbor(origin, pickups);
  const afterPickups = orderedPickups.length ? orderedPickups[orderedPickups.length - 1] : origin;
  const orderedDropoffs = bestOrder(afterPickups, dropoffs);
  return { stops: [...orderedPickups, ...orderedDropoffs], hasPickups: orderedPickups.length > 0, missing };
}

/**
 * Google Maps'te çok duraklı yol tarifi. Origin verilmezse cihazın konumundan başlar.
 * `dir_action=navigate` telefonda önizleme yerine doğrudan sesli navigasyonu başlatır.
 * Not: Google Maps mobil uygulaması en çok 3 ara durağı destekler.
 */
export function mapsMultiStopUrl(stops: LatLng[], navigate = true): string {
  const dest = stops[stops.length - 1];
  const waypoints = stops.slice(0, -1).map((s) => `${s.lat},${s.lng}`).join("|");
  const params = new URLSearchParams({ api: "1", destination: `${dest.lat},${dest.lng}`, travelmode: "driving" });
  if (waypoints) params.set("waypoints", waypoints);
  if (navigate) params.set("dir_action", "navigate");
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/** Google Maps uygulamasının mobilde desteklediği en fazla ara durak */
export const MOBILE_MAX_WAYPOINTS = 3;
